import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLanguage, T } from "@/lib/i18n";

const NAV = [
  { id: "about", key: "about" },
  { id: "campaigns", key: "campaigns" },
  { id: "news", key: "news" },
  { id: "events", key: "events" },
  { id: "leadership", key: "leadership" },
  { id: "gallery", key: "gallery" },
  { id: "contact", key: "contact" },
];

export default function Header() {
  const { lang, toggle } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 ${scrolled ? "glass-header" : "bg-white border-b border-sjm-line"}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-9 h-9 bg-sjm-red flex items-center justify-center">
            <span className="text-white font-display font-bold text-lg leading-none">সং</span>
          </div>
          <div className="leading-tight">
            <div className={`font-display font-bold text-base sm:text-lg text-sjm-ink ${lang === "bn" ? "font-bn" : ""}`}>
              {T.org_name[lang]}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sjm-muted">
              {T.org_tag[lang === "en" ? "bn" : "en"]}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-auto" data-testid="desktop-nav">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => handleNav(n.id)}
              className="px-3 py-2 text-sm font-medium text-sjm-ink hover:text-sjm-red transition-colors"
            >
              {T.nav[n.key][lang]}
            </button>
          ))}
        </nav>

        <div className="ml-auto lg:ml-0 flex items-center gap-2">
          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-sjm-ink hover:bg-sjm-ink hover:text-white transition-all"
            aria-label="Toggle language"
          >
            <Languages className="w-3.5 h-3.5" />
            {lang === "en" ? "বাং" : "EN"}
          </button>

          <button
            data-testid="join-cta-header"
            onClick={() => handleNav("join")}
            className="hidden sm:inline-flex px-4 py-2 text-xs font-bold uppercase tracking-wider bg-sjm-red text-white hover:bg-[#D62828] transition-all"
          >
            {T.hero.cta_join[lang]}
          </button>

          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sjm-line bg-white" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => handleNav(n.id)}
                className="text-left py-3 text-base font-medium border-b border-sjm-line"
              >
                {T.nav[n.key][lang]}
              </button>
            ))}
            <button
              onClick={() => handleNav("join")}
              data-testid="join-cta-mobile"
              className="mt-4 px-4 py-3 text-sm font-bold uppercase bg-sjm-red text-white"
            >
              {T.hero.cta_join[lang]}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
